import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Image as ImageIcon, ArrowUp, ArrowDown, Star, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { compressImages, formatBytes } from '../lib/imageCompress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SLOT_DRAG_TYPE = 'application/x-brainwave-slot';

let __slotSeq = 0;
export const nextSlotId = () => `slot_${Date.now()}_${++__slotSeq}`;

const resolveUrl = (u) => {
  if (!u) return '';
  return u.startsWith('http') || u.startsWith('data:') || u.startsWith('blob:') ? u : `${BACKEND_URL}${u}`;
};

/**
 * Build the request payload for a slot list.
 *  - Appends each new file under the given form-field name (default: 'images').
 *  - Returns { imageOrder, imageOverlays } JSON-stringified for direct FormData.append.
 */
export const buildSlotPayload = (slots, fd, fileFieldName = 'images') => {
  const order = [];
  let newIdx = 0;
  let hasFiles = false;
  slots.forEach((s) => {
    if (s.type === 'existing') {
      order.push({ type: 'existing', url: s.url });
    } else {
      fd.append(fileFieldName, s.file);
      order.push({ type: 'new', index: newIdx });
      newIdx += 1;
      hasFiles = true;
    }
  });
  fd.append('imageOrder', JSON.stringify(order));
  fd.append('imageOverlays', JSON.stringify(slots.map((s) => s.overlay || '')));
  return { hasFiles };
};

/**
 * ImageSlotManager — controlled slot list with file-picker, drag-and-drop file
 * upload, drag-to-reorder, set-main, move up/down, remove, and per-slot
 * overlay caption inputs. Also surfaces an upload progress bar when the
 * parent passes `uploadProgress`/`isUploading`.
 */
export const ImageSlotManager = ({
  slots,
  onSlotsChange,
  testIdPrefix = 'admin-images',
  uploadProgress = 0,
  isUploading = false,
  helperText,
}) => {
  const [compressing, setCompressing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [reorderDragIndex, setReorderDragIndex] = useState(null);
  const [reorderHoverIndex, setReorderHoverIndex] = useState(null);
  const dragDepthRef = useRef(0);

  const filesToSlots = (files) => Promise.all(files.map((file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve({
      id: nextSlotId(),
      type: 'new',
      file,
      preview: reader.result,
      overlay: '',
    });
    reader.readAsDataURL(file);
  })));

  const ingestFiles = async (files) => {
    const imageFiles = files.filter((f) => f && f.type && f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      if (files.length > 0) toast.error('Only image files are allowed');
      return;
    }
    setCompressing(true);
    try {
      const { files: compressed, originalBytes, compressedBytes } = await compressImages(imageFiles);
      const newSlots = await filesToSlots(compressed);
      onSlotsChange([...slots, ...newSlots]);
      const saved = originalBytes - compressedBytes;
      const savedPct = originalBytes > 0 ? Math.round((saved / originalBytes) * 100) : 0;
      if (saved > 50 * 1024) {
        toast.success(
          `Added ${newSlots.length} image${newSlots.length === 1 ? '' : 's'} — compressed ${formatBytes(originalBytes)} → ${formatBytes(compressedBytes)} (saved ${savedPct}%)`,
          { duration: 4000 }
        );
      } else {
        toast.success(`Added ${newSlots.length} image${newSlots.length === 1 ? '' : 's'}`);
      }
    } finally {
      setCompressing(false);
    }
  };

  const handleAddImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    await ingestFiles(files);
  };

  // ---- File drag-drop -----------------------------------------------------
  const handleDragEnter = (e) => {
    const types = Array.from(e.dataTransfer?.types || []);
    if (types.includes(SLOT_DRAG_TYPE)) return;
    if (!types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };
  const handleDragOver = (e) => {
    const types = Array.from(e.dataTransfer?.types || []);
    if (types.includes(SLOT_DRAG_TYPE)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    await ingestFiles(files);
  };

  // ---- Slot reorder -------------------------------------------------------
  const handleSlotDragStart = (idx) => (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(SLOT_DRAG_TYPE, String(idx));
    setReorderDragIndex(idx);
  };
  const handleSlotDragOver = (idx) => (e) => {
    if (!e.dataTransfer.types.includes(SLOT_DRAG_TYPE)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (reorderHoverIndex !== idx) setReorderHoverIndex(idx);
  };
  const handleSlotDrop = (idx) => (e) => {
    if (!e.dataTransfer.types.includes(SLOT_DRAG_TYPE)) return;
    e.preventDefault();
    e.stopPropagation();
    const from = parseInt(e.dataTransfer.getData(SLOT_DRAG_TYPE), 10);
    setReorderDragIndex(null);
    setReorderHoverIndex(null);
    if (Number.isNaN(from) || from === idx) return;
    const next = [...slots];
    const [picked] = next.splice(from, 1);
    next.splice(idx, 0, picked);
    onSlotsChange(next);
  };
  const handleSlotDragEnd = () => {
    setReorderDragIndex(null);
    setReorderHoverIndex(null);
  };

  // ---- Slot operations ----------------------------------------------------
  const moveSlot = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= slots.length) return;
    const next = [...slots];
    [next[idx], next[target]] = [next[target], next[idx]];
    onSlotsChange(next);
  };
  const setAsMain = (idx) => {
    if (idx <= 0 || idx >= slots.length) return;
    const next = [...slots];
    const [picked] = next.splice(idx, 1);
    next.unshift(picked);
    onSlotsChange(next);
    toast.success('Set as main image');
  };
  const removeSlot = (idx) => {
    onSlotsChange(slots.filter((_, i) => i !== idx));
  };
  const updateOverlay = (idx, value) => {
    onSlotsChange(slots.map((s, i) => (i === idx ? { ...s, overlay: value } : s)));
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative rounded-lg transition-colors ${isDragging ? 'ring-2 ring-offset-2' : ''}`}
      style={isDragging ? { boxShadow: '0 0 0 2px #00CED1' } : {}}
      data-testid={`${testIdPrefix}-dropzone`}
    >
      <div className="flex items-center justify-between mb-1">
        <Label htmlFor={`${testIdPrefix}-input`}>Images</Label>
        <span className="text-xs text-gray-500 flex items-center gap-2">
          {compressing && (
            <span
              className="flex items-center gap-1"
              style={{ color: '#00CED1' }}
              data-testid={`${testIdPrefix}-compressing`}
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              Compressing…
            </span>
          )}
          <span>{slots.length} image{slots.length === 1 ? '' : 's'}</span>
        </span>
      </div>
      <Input
        id={`${testIdPrefix}-input`}
        type="file"
        accept="image/*"
        multiple
        onChange={handleAddImages}
        data-testid={`${testIdPrefix}-add-input`}
      />
      <p className="text-xs text-gray-500 mt-1">
        {helperText || (
          <>
            The first image is the <strong>main</strong> picture (used as the cover everywhere).{' '}
            <span className="font-medium" style={{ color: '#00CED1' }}>
              Drag &amp; drop image files anywhere here to add them, or drag a slot card up/down to reorder.
            </span>{' '}
            Large images are automatically compressed before upload.
          </>
        )}
      </p>

      {slots.length === 0 && (
        <div
          className="mt-3 border-2 border-dashed rounded-lg p-8 text-center text-gray-500"
          style={{ borderColor: isDragging ? '#00CED1' : '#d1d5db', backgroundColor: isDragging ? 'rgba(0,206,209,0.05)' : 'transparent' }}
        >
          <ImageIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">Drag &amp; drop image files here, or use the file picker above.</p>
        </div>
      )}

      {slots.length > 0 && (
        <div className="mt-3 space-y-3" data-testid={`${testIdPrefix}-slots`}>
          {slots.map((slot, index) => (
            <div
              key={slot.id}
              className={`border rounded-lg p-2 bg-gray-50 transition-all ${
                reorderDragIndex === index ? 'opacity-40' : ''
              } ${
                reorderHoverIndex === index && reorderDragIndex !== null && reorderDragIndex !== index
                  ? 'border-2'
                  : ''
              }`}
              style={
                reorderHoverIndex === index && reorderDragIndex !== null && reorderDragIndex !== index
                  ? { borderColor: '#00CED1' }
                  : {}
              }
              draggable
              onDragStart={handleSlotDragStart(index)}
              onDragOver={handleSlotDragOver(index)}
              onDrop={handleSlotDrop(index)}
              onDragEnd={handleSlotDragEnd}
              data-testid={`${testIdPrefix}-slot-${index}`}
            >
              <div className="grid grid-cols-[8rem_1fr_auto] gap-3 items-start">
                <div className="relative cursor-grab active:cursor-grabbing">
                  <img
                    src={resolveUrl(slot.preview)}
                    alt={`Slot ${index + 1}`}
                    className="w-32 h-24 object-cover rounded"
                    draggable={false}
                  />
                  {index === 0 && (
                    <span
                      className="absolute top-1 left-1 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"
                      style={{ backgroundColor: '#00CED1' }}
                      data-testid={`${testIdPrefix}-main-badge-${index}`}
                    >
                      <Star className="h-3 w-3" /> Main
                    </span>
                  )}
                  {slot.type === 'new' && (
                    <span className="absolute bottom-1 left-1 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                      New
                    </span>
                  )}
                  {slot.overlay && (
                    <div className="absolute inset-x-0 bottom-0 px-2 py-1 bg-gradient-to-t from-black/70 to-transparent rounded-b">
                      <p className="text-white text-[10px] font-semibold drop-shadow line-clamp-2">{slot.overlay}</p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor={`overlay-${slot.id}`} className="text-xs">
                    Overlay caption (image #{index + 1})
                  </Label>
                  <Input
                    id={`overlay-${slot.id}`}
                    value={slot.overlay || ''}
                    onChange={(e) => updateOverlay(index, e.target.value)}
                    placeholder="Optional caption shown over this image"
                    className="mt-1"
                    data-testid={`${testIdPrefix}-overlay-${index}`}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <Button
                    type="button" variant="outline" size="icon" className="h-8 w-8"
                    title="Move up" disabled={index === 0}
                    onClick={() => moveSlot(index, -1)}
                    data-testid={`${testIdPrefix}-move-up-${index}`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button" variant="outline" size="icon" className="h-8 w-8"
                    title="Move down" disabled={index === slots.length - 1}
                    onClick={() => moveSlot(index, 1)}
                    data-testid={`${testIdPrefix}-move-down-${index}`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button" variant="outline" size="icon" className="h-8 w-8"
                    title={index === 0 ? 'This is the main picture' : 'Set as main picture'}
                    disabled={index === 0}
                    onClick={() => setAsMain(index)}
                    data-testid={`${testIdPrefix}-set-main-${index}`}
                    style={index === 0 ? {} : { borderColor: '#00CED1', color: '#00CED1' }}
                  >
                    <Star className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button" variant="outline" size="icon"
                    className="h-8 w-8 text-red-600 hover:text-red-700"
                    title="Remove" onClick={() => removeSlot(index)}
                    data-testid={`${testIdPrefix}-remove-${index}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drop overlay shown when dragging files over a populated slot area */}
      {isDragging && slots.length > 0 && (
        <div
          className="pointer-events-none absolute inset-0 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 206, 209, 0.12)', border: '2px dashed #00CED1' }}
        >
          <div className="bg-white px-4 py-2 rounded-md shadow font-semibold" style={{ color: '#00CED1' }}>
            Drop images to add
          </div>
        </div>
      )}

      {/* Upload progress (controlled by parent) */}
      {isUploading && uploadProgress > 0 && uploadProgress < 100 && (
        <div className="space-y-1 mt-3" data-testid={`${testIdPrefix}-upload-progress`}>
          <div className="flex justify-between items-center text-xs">
            <span className="font-medium" style={{ color: '#00CED1' }}>Uploading images…</span>
            <span className="text-gray-600">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded h-2 overflow-hidden">
            <div
              className="h-full rounded"
              style={{ width: `${uploadProgress}%`, backgroundColor: '#00CED1', transition: 'width 0.2s' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const slotsFromExistingImages = (images = [], overlays = []) =>
  images.map((url, i) => ({
    id: nextSlotId(),
    type: 'existing',
    url,
    preview: url,
    overlay: overlays[i] || '',
  }));
